import { useState } from "react";
import { supabase } from "./supabaseClient";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700;1,800&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}

.login-page{
  min-height:100vh;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  background:var(--bg);
  position:relative;
  overflow:hidden;
}
.login-page::before{
  content:'';
  position:absolute;
  inset:0;
  background-image:linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px);
  background-size:60px 60px;
  mask-image:radial-gradient(ellipse 80% 80% at 50% 50%,black 30%,transparent 100%);
  pointer-events:none;
}
.login-page::after{
  content:'';
  position:absolute;
  top:50%;left:50%;
  transform:translate(-50%,-50%);
  width:600px;height:400px;
  background:radial-gradient(ellipse,rgba(201,168,76,0.07) 0%,transparent 70%);
  pointer-events:none;
}

.login-card{
  position:relative;
  z-index:1;
  width:100%;
  max-width:440px;
  background:var(--surface);
  border:1px solid rgba(201,168,76,0.2);
  border-radius:20px;
  padding:3rem 2.5rem;
  box-shadow:0 40px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.08);
  display:flex;
  flex-direction:column;
  align-items:center;
}
.login-card::before{
  content:'';
  position:absolute;
  top:0;left:10%;right:10%;
  height:1px;
  background:linear-gradient(90deg,transparent,var(--gold),transparent);
  border-radius:1px;
}

.login-wordmark{
  font-family:var(--font-display);
  font-size:2.2rem;
  font-weight:900;
  letter-spacing:-0.03em;
  color:var(--text);
  margin-bottom:0.25rem;
}
.login-wordmark em{font-style:italic;color:var(--gold);}
.login-wordmark sup{font-family:var(--font-mono);font-size:0.5rem;color:var(--muted);vertical-align:super;}

.login-tagline{
  font-family:var(--font-mono);
  font-size:0.62rem;
  color:var(--muted);
  letter-spacing:0.14em;
  text-transform:uppercase;
  margin-bottom:2rem;
}

/* TABS */
.login-tabs{
  display:flex;
  width:100%;
  background:var(--surface2);
  border:1px solid var(--border);
  border-radius:10px;
  padding:3px;
  margin-bottom:2rem;
  gap:3px;
}
.login-tab{
  flex:1;
  background:transparent;
  border:none;
  border-radius:8px;
  padding:0.55rem;
  font-family:var(--font-mono);
  font-size:0.68rem;
  font-weight:500;
  letter-spacing:0.08em;
  color:var(--muted);
  cursor:pointer;
  transition:all 0.18s;
  text-transform:uppercase;
}
.login-tab.active{
  background:var(--charcoal);
  color:var(--gold);
  border:1px solid rgba(201,168,76,0.2);
}

/* FORM */
.login-form{width:100%;display:flex;flex-direction:column;gap:1rem;}
.login-field{display:flex;flex-direction:column;gap:0.4rem;}
.login-field label{
  font-family:var(--font-mono);
  font-size:0.62rem;
  color:var(--muted);
  letter-spacing:0.1em;
  text-transform:uppercase;
}
.login-field input{
  background:var(--surface2);
  border:1px solid var(--border);
  border-radius:8px;
  padding:0.75rem 1rem;
  font-family:var(--font-body);
  font-size:0.9rem;
  color:var(--text);
  outline:none;
  transition:border-color 0.18s;
  width:100%;
}
.login-field input:focus{border-color:rgba(201,168,76,0.4);}
.login-field input::placeholder{color:var(--muted);}

.login-submit{
  width:100%;
  background:linear-gradient(135deg,var(--gold-dim),var(--gold));
  border:none;
  border-radius:10px;
  padding:0.85rem;
  font-family:var(--font-mono);
  font-size:0.75rem;
  font-weight:600;
  letter-spacing:0.1em;
  text-transform:uppercase;
  color:#09090e;
  cursor:pointer;
  transition:all 0.18s;
  margin-top:0.5rem;
}
.login-submit:hover{filter:brightness(1.1);transform:translateY(-1px);}
.login-submit:active{transform:translateY(0);}
.login-submit:disabled{opacity:0.5;cursor:not-allowed;transform:none;}

.login-error{
  background:var(--red-bg);
  border:1px solid rgba(192,57,43,0.3);
  border-radius:8px;
  padding:0.75rem 1rem;
  font-family:var(--font-mono);
  font-size:0.68rem;
  color:#f87171;
  letter-spacing:0.04em;
  width:100%;
}
.login-success{
  background:var(--green-bg);
  border:1px solid rgba(46,125,82,0.3);
  border-radius:8px;
  padding:0.75rem 1rem;
  font-family:var(--font-mono);
  font-size:0.68rem;
  color:#4ade80;
  letter-spacing:0.04em;
  width:100%;
}

.login-divider{
  width:100%;
  display:flex;
  align-items:center;
  gap:0.75rem;
  margin:0.25rem 0;
}
.login-divider span{
  font-family:var(--font-mono);
  font-size:0.6rem;
  color:var(--muted);
  white-space:nowrap;
}
.login-divider::before,.login-divider::after{
  content:'';
  flex:1;
  height:1px;
  background:var(--border);
}

.login-forgot{
  background:transparent;
  border:none;
  color:var(--muted);
  font-family:var(--font-mono);
  font-size:0.62rem;
  cursor:pointer;
  letter-spacing:0.06em;
  transition:color 0.18s;
  align-self:flex-end;
  margin-top:-0.5rem;
}
.login-forgot:hover{color:var(--gold);}

.login-back{
  background:transparent;
  border:none;
  color:var(--muted);
  font-family:var(--font-mono);
  font-size:0.62rem;
  cursor:pointer;
  letter-spacing:0.08em;
  transition:color 0.2s;
  margin-top:1.5rem;
}
.login-back:hover{color:var(--text);}

.login-hint{
  font-family:var(--font-mono);
  font-size:0.58rem;
  color:rgba(255,255,255,0.15);
  margin-top:0.75rem;
  letter-spacing:0.06em;
}

/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}

@media(max-width:768px){
  .login-card{width:90vw;padding:1.5rem;}
  .login-logo{font-size:1.6rem;}
}`;

export default function LoginPage({ onNavigate }) {
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetMode, setResetMode] = useState(false);

  const clearMessages = () => { setError(""); setSuccess(""); };

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) { setError(error.message); return; }
    // App.js auth listener will handle navigation
  };

  const handleSignup = async () => {
    if (!fullName || !email || !password) { setError("Please fill in all required fields."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } }
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSuccess("Account created! Check your email to confirm your address, then sign in.");
    setTab("login");
    setPassword("");
  };

  const handleReset = async () => {
    if (!email) { setError("Enter your email address to reset your password."); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSuccess("Password reset email sent. Check your inbox.");
    setResetMode(false);
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="login-page">
        <div className="login-card">
          <div className="login-wordmark">Whip<em>Count</em><sup>™</sup></div>
          <div className="login-tagline">Michigan Legislative Intelligence</div>

          {resetMode ? (
            <div className="login-form">
              {error && <div className="login-error">{error}</div>}
              {success && <div className="login-success">{success}</div>}
              <div className="login-field">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="you@yourfirm.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); clearMessages(); }}
                />
              </div>
              <button className="login-submit" onClick={handleReset} disabled={loading}>
                {loading ? "Sending..." : "Send Reset Email"}
              </button>
              <button className="login-forgot" onClick={() => { setResetMode(false); clearMessages(); }}>
                ← Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <div className="login-tabs">
                <button className={`login-tab${tab === "login" ? " active" : ""}`} onClick={() => { setTab("login"); clearMessages(); }}>
                  Sign In
                </button>
                <button className={`login-tab${tab === "signup" ? " active" : ""}`} onClick={() => { setTab("signup"); clearMessages(); }}>
                  Create Account
                </button>
              </div>

              <div className="login-form">
                {error && <div className="login-error">{error}</div>}
                {success && <div className="login-success">{success}</div>}

                {tab === "signup" && (
                  <>
                    <div className="login-field">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        placeholder="Jane Smith"
                        value={fullName}
                        onChange={e => { setFullName(e.target.value); clearMessages(); }}
                      />
                    </div>
                    <div className="login-field">
                      <label>Organization</label>
                      <input
                        type="text"
                        placeholder="Your firm or organization"
                        value={organization}
                        onChange={e => { setOrganization(e.target.value); clearMessages(); }}
                      />
                    </div>
                  </>
                )}

                <div className="login-field">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="you@yourfirm.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); clearMessages(); }}
                  />
                </div>

                <div className="login-field">
                  <label>Password *</label>
                  <input
                    type="password"
                    placeholder={tab === "signup" ? "Minimum 8 characters" : "Your password"}
                    value={password}
                    onChange={e => { setPassword(e.target.value); clearMessages(); }}
                    onKeyDown={e => e.key === "Enter" && (tab === "login" ? handleLogin() : handleSignup())}
                  />
                </div>

                {tab === "login" && (
                  <button className="login-forgot" onClick={() => { setResetMode(true); clearMessages(); }}>
                    Forgot password?
                  </button>
                )}

                <button
                  className="login-submit"
                  onClick={tab === "login" ? handleLogin : handleSignup}
                  disabled={loading}
                >
                  {loading ? "Please wait..." : tab === "login" ? "Sign In →" : "Create Account →"}
                </button>
              </div>
            </>
          )}

          <button className="login-back" onClick={() => onNavigate("home")}>
            ← Back to Home
          </button>
          <div className="login-hint">Subscriber access only</div>
        </div>
      </div>
    </>
  );
}
